/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useImperativeHandle, forwardRef, useContext, useState } from "react";
import { Dialog, CardContent, Card } from "@mui/material";
import { DocumentEditContext } from "cms/documents/document-selector/context/document-edit-context";
import { DocumentDialogBottomActions } from "./actions/document-dialog-bottom-actions";
import { DocumentSelectorClientContext } from "cms/documents/document-selector/context/document-selector-client-context";
import { GalleryContext } from "cms/documents/document-selector/context/gallery-context";
import { useSnackbar } from "hooks/useSnackbar";
import { DocumentEditHeader } from "./panels/document-edit-header";
import { DocumentDialogPreviewPanel } from "./panels/document-dialog-preview-panel";
import { DocumentEditPanel } from "./panels/document-edit-panel";
import { DocumentEditArchivePanel } from "./panels/document-edit-archive-panel";
import { useDocuments } from "cms/documents/hooks/use-document";
import { DocumentEditReplacePanel } from "./panels/document-edit-replace-panel";
import { DocumentRenamePanel } from "./panels/document-rename-panel";

const DocumentEditDialog = forwardRef((props, ref) => {

    const { showSnackbar } = useSnackbar();
    const { archiveDocument } = useDocuments();

    const {
        openEdit,
        handleEditClose,
        setOpenEdit,
        selectedDocument,
        setSelectedDocument,
        isReplacing,
        isArchiving,
        onDocumentArchived,
    } = useContext(DocumentEditContext);
    const isEdit = !isArchiving && !isReplacing;
    const hasFooter = !isArchiving && !isReplacing;
    
    const { handleReload } = useContext(DocumentSelectorClientContext);
    const [loading, setLoading] = useState(false);
    const [fileNameExist, setFileNameExist] = useState(false);
    const [renameFileNameValid, setRenameFileNameValid] = useState(true);

    const handleCancel = (event) => {
        handleEditClose(event);
    };

    const handleCloseDialog = () => {
        setFileNameExist(false);
        setRenameFileNameValid(true);
    };

    useImperativeHandle(ref, () => ({
        handleCloseDialog,
    }));

    const handleArchive = async () => {
        setLoading(true);
        const archived_doc = await archiveDocument(selectedDocument?.guid);
        await onDocumentArchived(archived_doc);
        setOpenEdit(false);
        setLoading(false);
    };

    const galleryContext = {
        selectedDocument,
        setSelectedDocument,
        showSnackbar,
    };

    const documentSelectorClientContext = {
        handleReload,
    };

    return (
        <DocumentSelectorClientContext.Provider value={documentSelectorClientContext}>
            <Dialog
                open={openEdit}
                onClose={handleCancel}
                fullWidth={true}
                maxWidth="md"
                className="simplicity-document-selector-overlay"
            >
                <GalleryContext.Provider value={galleryContext}>
                    <Card 
                        elevation={0}
                        className={`document-dialog-edit-window ${isEdit ? "simplicity-document-edit-window": ""} ${isArchiving ? "simplicity-document-archive-window": ""} ${isReplacing ? "simplicity-document-replace-window": ""}`}
                    >
                        <CardContent sx={{ flex: "1 0 auto", height: "100%" }}>
                            
                            <DocumentEditHeader />
                            
                            <DocumentRenamePanel
                                fileNameExist={fileNameExist}
                                setFileNameExist={setFileNameExist}
                                renameFileNameValid={renameFileNameValid}
                                setRenameFileNameValid={setRenameFileNameValid}
                                setLoading={setLoading}
                                loading={loading}
                            />
                            
                            <div className="simplicity-document-edit-window-content">
                                {isArchiving && (
                                    <DocumentEditArchivePanel 
                                        handleArchive={handleArchive}
                                        loading={loading}
                                    />
                                )}
                                {isReplacing && (
                                    <DocumentEditReplacePanel />
                                )}
                                {isEdit && (
                                    <>
                                        <DocumentDialogPreviewPanel />
                                        <div className="simplicity-doc-selector-window-active document-edit-panel">
                                            <DocumentEditPanel />
                                        </div>
                                    </>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                    
                    {hasFooter &&
                        <div className={`base-dialog-footer ${!fileNameExist && !renameFileNameValid ? "error-exist-footer" : ""}`}>
                            {fileNameExist && 
                                <div className="document-file-name-exist">
                                    Error: Filename already exists
                                </div>
                            }
                            {!renameFileNameValid && 
                                <div className="document-file-name-invalid">
                                    Error: filename doesn’t meet requirements, eg. one-two2-three-4.jpg
                                </div>
                            }
                            <DocumentDialogBottomActions />
                        </div>
                    }
                </GalleryContext.Provider>
            </Dialog>
        </DocumentSelectorClientContext.Provider>
    );
});

export { DocumentEditDialog };
